import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Paper,
  Divider,
  Select,
  MenuItem,
  ClickAwayListener,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Work,
  LocationOn,
  ExpandMore,
  Search,
  FilterList,
  Business,
  CurrencyRupee,
  Close,
  Assessment,
  Apartment,
  Paid,
} from "@mui/icons-material";
import { useAuth, api } from "../context/AuthContext";

const JOBS_PER_PAGE = 6;

const WORK_MODES = [
  { label: "Work From Office", value: "office" },
  { label: "Hybrid", value: "hybrid" },
  { label: "Remote", value: "remote" },
];
const LOCATIONS = ["Hyderabad", "Bangalore", "Delhi / NCR"];
const STIPENDS = [
  "Unpaid",
  "0–10k",
  "10k–20k",
  "20k–30k",
  "30k–40k",
  "40k–50k",
  "50k and above",
];
const SALARY_RANGES = [
  { label: "3–6 LPA", min: 3, max: 6 },
  { label: "6–10 LPA", min: 6, max: 10 },
  { label: "10–15 LPA", min: 10, max: 15 },
  { label: "15–25 LPA", min: 15, max: 25 },
];

const DEPARTMENT_MAPPING = {
  "Engineering - Software & QA": [
    "software", "developer", "qa", "testing", "engineer", "frontend", "backend", "full stack", 
    "mobile", "android", "ios", "web", "devops", "cloud", "security", "infrastructure",
    "data engineer", "machine learning", "ai", "artificial intelligence"
  ],
  "Sales & Business Development": ["sales", "business development", "account manager", "inside sales", "field sales"],
  "Marketing & Communication": ["marketing", "seo", "social media", "content", "digital marketing", "pr", "branding"],
  "Human Resources": ["hr", "recruiter", "talent acquisition", "human resources", "payroll"],
  "Finance & Accounting": ["finance", "accounting", "tax", "audit", "chartered accountant", "tally"],
  "Data Science & Analytics": ["data science", "analytics", "data analyst", "business analyst", "statistics"],
  "UX, Design & Architecture": ["ux", "ui", "design", "graphic designer", "product design", "architect"],
  "Customer Success, Service & Operations": ["customer success", "operations", "support", "customer service", "bpo", "kpo"],
  "Project & Program Management": ["project manager", "program manager", "scrum master", "agile"],
  "Teaching & Training": ["teacher", "trainer", "professor", "education", "mentor"],
  "Healthcare & Life Sciences": ["doctor", "nurse", "healthcare", "pharma", "medical"],
  "Legal & Regulatory": ["legal", "lawyer", "compliance", "regulatory"],
  "Product Management": ["product manager", "product owner"],
};

const getMappedDepartment = (job) => {
  if (job.department) {
    const matched = Object.keys(DEPARTMENT_MAPPING).find(dept => 
      dept.toLowerCase() === job.department.toLowerCase()
    );
    if (matched) return matched;
  }

  const title = job.title.toLowerCase();
  for (const [dept, keywords] of Object.entries(DEPARTMENT_MAPPING)) {
    if (keywords.some(keyword => title.includes(keyword))) {
      return dept;
    }
  }
  return "Other";
};

function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem("jobsFilters");
    return savedFilters ? JSON.parse(savedFilters) : {
      workMode: [],
      experience: -1,
      department: [],
      location: [],
      salary: [],
      stipend: [],
    };
  });

  useEffect(() => {
    sessionStorage.setItem("jobsFilters", JSON.stringify(filters));
  }, [filters]);

  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem("jobsSearchQuery") || "";
  });

  useEffect(() => {
    sessionStorage.setItem("jobsSearchQuery", searchQuery);
  }, [searchQuery]);

  const [location, setLocation] = useState(() => {
    return sessionStorage.getItem("jobsLocation") || "";
  });

  useEffect(() => {
    sessionStorage.setItem("jobsLocation", location);
  }, [location]);

  const [experience, setExperience] = useState(() => {
    const savedExp = sessionStorage.getItem("jobsExperience");
    return savedExp ? parseInt(savedExp, 10) : -1;
  });

  useEffect(() => {
    sessionStorage.setItem("jobsExperience", experience);
  }, [experience]);

  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem("jobsPage");
    return savedPage ? parseInt(savedPage, 10) : 1;
  });

  useEffect(() => {
    sessionStorage.setItem("jobsPage", currentPage);
  }, [currentPage]);

  const [modalOpen, setModalOpen] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const [modalFilterType, setModalFilterType] = useState(null);
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [tempModalFilters, setTempModalFilters] = useState([]);
  const [isApplyEnabled, setIsApplyEnabled] = useState(false);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    async function fetchJobs() {
      try {
        const response = await api.get("/jobs");
        const sortedJobs = response.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setJobs(sortedJobs);
      } catch (error) {
        console.error("Failed to fetch jobs:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchJobs();
  }, []);

  const handleFilterChange = (category, value) => {
    setFilters((prev) => {
      const current = prev[category];
      if (Array.isArray(current)) {
        const updated = current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value];
        return { ...prev, [category]: updated };
      }
      return { ...prev, [category]: value };
    });
    
    // Sync top-level experience state if sidebar experience changes
    if (category === "experience") {
      setExperience(value);
      setFilters((prev) => ({ ...prev, experience: value }));
    }
    
    setCurrentPage(1);
    setIsSearchExpanded(false);
  };

  const getCounts = (category) => {
    const counts = {};
    const baseJobs = jobs; // We want counts based on all available jobs, or should it be filtered? Naukri usually shows counts based on other active filters.
    
    baseJobs.forEach((job) => {
      let values = [];
      if (category === "department") {
        const dept = getMappedDepartment(job);
        values = [dept];
      } else if (category === "location") {
        values = job.location ? [job.location] : [];
      } else if (category === "salary") {
        const range = SALARY_RANGES.find((r) => {
          const salaryValue = parseInt(job.salary);
          return salaryValue >= r.min && salaryValue <= r.max;
        });
        if (range) values = [range.label];
      } else if (category === "workMode") {
        values = job.work_mode ? [job.work_mode] : [];
      } else if (category === "stipend") {
        values = job.stipend ? [job.stipend] : [];
      }

      values.forEach((val) => {
        counts[val] = (counts[val] || 0) + 1;
      });
    });
    return counts;
  };

  const deptCounts = getCounts("department");
  const locCounts = getCounts("location");
  const salCounts = getCounts("salary");
  const workModeCounts = getCounts("workMode");
  const stipendCounts = getCounts("stipend");

  const allDepartments = Array.from(new Set([
    ...Object.keys(DEPARTMENT_MAPPING),
    ...Object.keys(deptCounts)
  ])).sort((a, b) => {
    // Show departments with jobs first, then alphabetically
    const countA = deptCounts[a] || 0;
    const countB = deptCounts[b] || 0;
    if (countB !== countA) return countB - countA;
    return a.localeCompare(b);
  });
  const allLocations = Array.from(new Set([
    ...LOCATIONS,
    ...Object.keys(locCounts)
  ])).sort((a, b) => {
    const countA = locCounts[a] || 0;
    const countB = locCounts[b] || 0;
    if (countB !== countA) return countB - countA;
    return a.localeCompare(b);
  });

  const handleOpenFilterModal = (type) => {
    setModalFilterType(type);
    setTempModalFilters(filters[type]);
    setModalSearchQuery("");
    setIsApplyEnabled(false);
  };

  const handleModalCheckboxChange = (value) => {
    setTempModalFilters((prev) => {
      const updated = prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value];
      
      // Check if updated matches current filters to enable/disable Apply
      const currentFilters = filters[modalFilterType];
      const isChanged = updated.length !== currentFilters.length || 
                        !updated.every(v => currentFilters.includes(v));
      setIsApplyEnabled(isChanged);
      
      return updated;
    });
  };

  const handleApplyModalFilters = () => {
    setFilters((prev) => ({ ...prev, [modalFilterType]: tempModalFilters }));
    setModalFilterType(null);
    setCurrentPage(1);
    setIsSearchExpanded(false);
  };

  const handleResetFilters = () => {
    const initialFilters = {
      workMode: [],
      experience: -1,
      department: [],
      location: [],
      salary: [],
      stipend: [],
    };
    setFilters(initialFilters);
    setSearchQuery("");
    setLocation("");
    setExperience(-1);
    setCurrentPage(1);
    sessionStorage.removeItem("jobsFilters");
    sessionStorage.removeItem("jobsSearchQuery");
    sessionStorage.removeItem("jobsLocation");
    sessionStorage.removeItem("jobsExperience");
    sessionStorage.removeItem("jobsPage");
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         job.company_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesLocationSearch = !location || job.location?.toLowerCase().includes(location.toLowerCase());

    const matchesWorkMode = filters.workMode.length === 0 || 
                           filters.workMode.some(mode => job.work_mode?.toLowerCase() === mode.toLowerCase());
    
    // Combined Experience Filter
    const matchesExperience = filters.experience === -1 || (
      job.experience_range ? (() => {
        const [min, max] = job.experience_range.split("-").map(Number);
        const expValue = parseInt(filters.experience);
        // If user selects 0 (Fresher), show jobs where the range includes 0
        if (expValue === 0) {
          return min === 0;
        }
        // For other years, match if it falls within the range
        return expValue >= min && expValue <= max;
      })() : true
    );
    
    const matchesDepartment = filters.department.length === 0 || 
                             filters.department.includes(getMappedDepartment(job));

    const matchesLocationSidebar = filters.location.length === 0 || 
                           filters.location.some(loc => job.location?.toLowerCase().includes(loc.toLowerCase()));

    const matchesSalary = filters.salary.length === 0 || filters.salary.some(rangeLabel => {
      const range = SALARY_RANGES.find(r => r.label === rangeLabel);
      if (!range || !job.salary) return false;
      // Extract numeric value from salary string like "7 LPA"
      const salaryValue = parseInt(job.salary); 
      return salaryValue >= range.min && salaryValue <= range.max;
    });

    const matchesStipend = filters.stipend.length === 0 || 
                          filters.stipend.includes(job.stipend);

    return matchesSearch && matchesLocationSearch && matchesWorkMode && matchesExperience && matchesDepartment && matchesLocationSidebar && matchesSalary && matchesStipend;
  });

  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * JOBS_PER_PAGE,
    currentPage * JOBS_PER_PAGE
  );

  const FilterSection = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Header for All Filters */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
        <Typography variant="subtitle1" fontWeight="700" color="text.primary">All Filters</Typography>
        {Object.values(filters).some(f => Array.isArray(f) ? f.length > 0 : f !== -1) && (
          <Typography 
            variant="caption" 
            fontWeight="700" 
            color="primary.main" 
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={handleResetFilters}
          >
            Applied ({
              Object.values(filters).reduce((acc, curr) => acc + (Array.isArray(curr) ? curr.length : (curr !== -1 ? 1 : 0)), 0)
            })
          </Typography>
        )}
      </Box>

      {/* Department Section - NOW AT THE TOP */}
      <Paper elevation={0} sx={{ borderBottom: "1px solid #f0f0f0", borderRadius: 0, mb: 2 }}>
        <Accordion defaultExpanded sx={{ boxShadow: "none", "&:before": { display: "none" }, bgcolor: 'transparent' }}>
          <AccordionSummary 
            expandIcon={<ExpandMore sx={{ fontSize: 20 }} />} 
            sx={{ px: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 }, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography variant="subtitle2" fontWeight="700" color="text.primary">Department</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pb: 2, pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {allDepartments.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', py: 1 }}>
                  No categories available
                </Typography>
              ) : (
                (allDepartments.slice(0, 5)).map((dept) => (
                  <FormControlLabel
                    key={dept}
                    control={
                      <Checkbox
                        checked={filters.department.includes(dept)}
                        onChange={() => handleFilterChange("department", dept)}
                        size="small"
                        sx={{ p: 0.5, color: '#666', '&.Mui-checked': { color: 'text.primary' } }}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" sx={{ 
                          color: filters.department.includes(dept) ? 'text.primary' : 'text.secondary',
                          fontSize: '0.8125rem',
                          fontWeight: filters.department.includes(dept) ? 700 : 400
                        }}>
                          {dept}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                          ({deptCounts[dept] || 0})
                        </Typography>
                      </Box>
                    }
                    sx={{ width: "100%", mr: 0, ml: 0, mb: 0.5 }}
                  />
                ))
              )}
              {allDepartments.length > 0 && (
                <Typography 
                  variant="caption" 
                  onClick={() => handleOpenFilterModal("department")}
                  sx={{ 
                    cursor: "pointer", 
                    mt: 1, 
                    fontWeight: "700", 
                    color: 'primary.main', 
                    fontSize: '0.8125rem',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  View More
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Work mode Section - IN THE MIDDLE */}
      <Paper elevation={0} sx={{ borderBottom: "1px solid #f0f0f0", borderRadius: 0, mb: 2 }}>
        <Accordion defaultExpanded sx={{ boxShadow: "none", "&:before": { display: "none" }, bgcolor: 'transparent' }}>
          <AccordionSummary 
            expandIcon={<ExpandMore sx={{ fontSize: 20 }} />} 
            sx={{ px: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 }, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography variant="subtitle2" fontWeight="700" color="text.primary">Work mode</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pb: 2, pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {WORK_MODES.map((mode) => (
                <FormControlLabel
                  key={mode.value}
                  control={
                    <Checkbox
                      checked={filters.workMode.includes(mode.value)}
                      onChange={() => handleFilterChange("workMode", mode.value)}
                      size="small"
                      sx={{ p: 0.5, color: '#666', '&.Mui-checked': { color: 'text.primary' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ 
                        color: filters.workMode.includes(mode.value) ? 'text.primary' : 'text.secondary', 
                        fontSize: '0.8125rem',
                        fontWeight: filters.workMode.includes(mode.value) ? 700 : 400
                      }}>{mode.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                        ({workModeCounts[mode.value] || 0})
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "100%", mr: 0, ml: 0, mb: 0.5 }}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Experience Section - NOW AT THE BOTTOM */}
      <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: "12px", overflow: "hidden" }}>
              <Accordion defaultExpanded sx={{ boxShadow: "none", "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2, minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Assessment sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight="700" color="text.primary">Experience</Typography>
                  </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2, pt: 0 }}>
                      <Box sx={{ pt: 1 }}>
                        <Typography variant="body2" color="primary.main" fontWeight="700" sx={{ mb: 1, fontSize: '0.875rem' }}>
                          {filters.experience === -1 ? "0 - 15 Years" : `${filters.experience} Years`}
                        </Typography>
              <Slider
                 value={filters.experience === -1 ? 0 : filters.experience}
                onChange={(e, val) => handleFilterChange("experience", val)}
                min={0}
                max={15}
                size="small"
                sx={{
                  height: 4,
                  '& .MuiSlider-thumb': {
                    width: 16,
                     height: 16,
                     bgcolor: '#fff',
                     border: '2px solid currentColor',
                     '&:hover': { boxShadow: '0 0 0 8px rgba(0, 139, 139, 0.16)' },
                  },
                  '& .MuiSlider-track': { border: 'none' },
                   '& .MuiSlider-rail': { opacity: 0.5, bgcolor: '#bfbfbf' },
                }}
              />
              {/* Custom Tooltip/Value inside thumb area */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                <Typography variant="caption" color="text.disabled">0 Years</Typography>
                 <Typography variant="caption" color="text.disabled">15 Years</Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper elevation={0} sx={{ borderBottom: "1px solid #f0f0f0", borderRadius: 0, mb: 2 }}>
        <Accordion defaultExpanded sx={{ boxShadow: "none", "&:before": { display: "none" }, bgcolor: 'transparent' }}>
          <AccordionSummary 
            expandIcon={<ExpandMore sx={{ fontSize: 20 }} />} 
            sx={{ px: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 }, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography variant="subtitle2" fontWeight="700" color="text.primary">Location</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pb: 2, pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {(allLocations.slice(0, 5)).map((loc) => (
                <FormControlLabel
                  key={loc}
                  control={
                    <Checkbox
                      checked={filters.location.includes(loc)}
                      onChange={() => handleFilterChange("location", loc)}
                      size="small"
                      sx={{ p: 0.5, color: '#666', '&.Mui-checked': { color: 'text.primary' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ 
                        color: filters.location.includes(loc) ? 'text.primary' : 'text.secondary', 
                        fontSize: '0.8125rem',
                        fontWeight: filters.location.includes(loc) ? 700 : 400
                      }}>{loc}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                        ({locCounts[loc] || 0})
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "100%", mr: 0, ml: 0, mb: 0.5 }}
                />
              ))}
              {allLocations.length > 5 && (
                <Typography 
                  variant="caption" 
                  onClick={() => handleOpenFilterModal("location")}
                  sx={{ 
                    cursor: "pointer", 
                    mt: 1, 
                    fontWeight: "700", 
                    color: 'primary.main', 
                    fontSize: '0.8125rem',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  View More
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper elevation={0} sx={{ borderBottom: "1px solid #f0f0f0", borderRadius: 0, mb: 2 }}>
        <Accordion defaultExpanded sx={{ boxShadow: "none", "&:before": { display: "none" }, bgcolor: 'transparent' }}>
          <AccordionSummary 
            expandIcon={<ExpandMore sx={{ fontSize: 20 }} />} 
            sx={{ px: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 }, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography variant="subtitle2" fontWeight="700" color="text.primary">Salary (LPA)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pb: 2, pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {SALARY_RANGES.map((range) => (
                <FormControlLabel
                  key={range.label}
                  control={
                    <Checkbox
                      checked={filters.salary.includes(range.label)}
                      onChange={() => handleFilterChange("salary", range.label)}
                      size="small"
                      sx={{ p: 0.5, color: '#666', '&.Mui-checked': { color: 'text.primary' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ 
                        color: filters.salary.includes(range.label) ? 'text.primary' : 'text.secondary', 
                        fontSize: '0.8125rem',
                        fontWeight: filters.salary.includes(range.label) ? 700 : 400
                      }}>{range.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                        ({salCounts[range.label] || 0})
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "100%", mr: 0, ml: 0, mb: 0.5 }}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Paper elevation={0} sx={{ borderBottom: "1px solid #f0f0f0", borderRadius: 0, mb: 2 }}>
        <Accordion defaultExpanded sx={{ boxShadow: "none", "&:before": { display: "none" }, bgcolor: 'transparent' }}>
          <AccordionSummary 
            expandIcon={<ExpandMore sx={{ fontSize: 20 }} />} 
            sx={{ px: 1, minHeight: 40, '&.Mui-expanded': { minHeight: 40 }, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography variant="subtitle2" fontWeight="700" color="text.primary">Stipend</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pb: 2, pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {STIPENDS.map((stipend) => (
                <FormControlLabel
                  key={stipend}
                  control={
                    <Checkbox
                      checked={filters.stipend.includes(stipend)}
                      onChange={() => handleFilterChange("stipend", stipend)}
                      size="small"
                      sx={{ p: 0.5, color: '#666', '&.Mui-checked': { color: 'text.primary' } }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ 
                        color: filters.stipend.includes(stipend) ? 'text.primary' : 'text.secondary', 
                        fontSize: '0.8125rem',
                        fontWeight: filters.stipend.includes(stipend) ? 700 : 400
                      }}>{stipend}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                        ({stipendCounts[stipend] || 0})
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "100%", mr: 0, ml: 0, mb: 0.5 }}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <Button
        variant="contained"
        fullWidth
        startIcon={<FilterList />}
        sx={{
          mt: 1,
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: '700',
          py: 1,
          '&:hover': {
            bgcolor: 'primary.dark',
          }
        }}
        onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        Apply Filters
      </Button>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 2, px: { xs: 2, md: 4 }, mt: 0, bgcolor: '#fafafa', minHeight: '100vh' }}>
      {/* Header Alignment: FILTERS, Reset All, and Explore Opportunities */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Box sx={{ width: { md: "20.83%", lg: "18.75%" }, display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "800", color: "text.primary", mt: 0, letterSpacing: '0.5px' }}>
            FILTERS
          </Typography>
          <Button 
            variant="text" 
            size="small" 
            onClick={handleResetFilters}
            sx={{ textTransform: "none", fontWeight: "700", minWidth: "auto", p: 0, color: 'error.main', fontSize: '0.75rem' }}
          >
            Reset All
          </Button>
        </Box>
        <Box sx={{ flexGrow: 1, pl: { md: 4 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: "900",
              color: "text.primary",
              textTransform: "none",
              mt: 0,
              letterSpacing: '-1px',
              fontSize: { xs: '1.5rem', md: '1.75rem' }
            }}
          >
            Explore Opportunities
          </Typography>
        </Box>
      </Box>

      <Grid container sx={{ alignItems: "flex-start" }}>
        {/* Left Sidebar - Filters */}
        {!isMobile && (
          <Grid 
            item 
            md={3} 
            lg={2.5} 
            sx={{ 
              position: "sticky", 
              top: 64, 
              height: "calc(100vh - 120px)", 
              overflowY: "auto",
              pt: 0,
              pb: 2,
              pr: 2,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: "#bbb",
                },
              },
            }}
          >
            <FilterSection />
          </Grid>
        )}

        {/* Right Section - Jobs */}
        <Grid item xs={12} md={9} lg={9.5} sx={{ pt: 0, pb: 4, pl: { md: 4 }, pr: { md: 2 }, mt: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: -6, // Pull up to align with header row if needed, or keep it for search box
              mb: 2,
              flexWrap: "wrap",
              gap: 2,
              position: 'relative',
              zIndex: 2
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", width: { xs: "100%", md: isSearchExpanded ? "100%" : "auto" }, transition: "width 0.3s ease" }}>
              <ClickAwayListener
                onClickAway={(event) => {
                  // ✅ Ignore clicks on MUI dropdown (Select menu) or other portals
                  const isMenuClick = 
                    event.target.closest('[role="listbox"]') ||
                    event.target.closest(".MuiPopover-root") ||
                    event.target.closest(".MuiMenu-paper") ||
                    event.target.closest(".MuiList-root");

                  if (isMenuClick) {
                    return;
                  }

                  setIsSearchExpanded(false);
                }}
              >
                <Paper
                  elevation={isSearchExpanded ? 8 : 1}
                  sx={{
                    display: "flex",
                    flexDirection: isMobile && isSearchExpanded ? "column" : "row",
                    alignItems: "center",
                    borderRadius: isSearchExpanded && isMobile ? "16px" : "100px",
                    border: "1px solid",
                    borderColor: isSearchExpanded ? "primary.main" : "#e0e0e0",
                    bgcolor: "background.paper",
                    width: isSearchExpanded ? "100%" : { xs: "100%", sm: 350 },
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: isSearchExpanded ? "0 10px 30px rgba(0, 139, 139, 0.15)" : "0 2px 10px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                    p: isSearchExpanded ? 0.5 : 0,
                    ...(isMobile && isSearchExpanded && { p: 2, gap: 2 })
                  }}
                >
                  {!isSearchExpanded ? (
                    <TextField
                      variant="standard"
                      size="small"
                      placeholder="Search jobs, companies..."
                      value={searchQuery}
                      onClick={() => setIsSearchExpanded(true)}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                          <InputAdornment position="start" sx={{ ml: 2 }}>
                            <Search sx={{ color: 'primary.main' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ width: "100%", py: 1.2 }}
                    />
                  ) : (
                    <>
                      {/* Job Role Input */}
                      <Box sx={{ flex: 1.5, display: "flex", alignItems: "center", px: 2, width: "100%" }}>
                        <Search sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                        <TextField
                          variant="standard"
                          fullWidth
                          placeholder="Skills, Designation..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                          }}
                          InputProps={{ disableUnderline: true }}
                          autoFocus
                        />
                      </Box>
                      
                      {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 30, alignSelf: "center" }} />}

                      {/* Experience Dropdown */}
                      <Box sx={{ flex: 1, display: "flex", alignItems: "center", px: 2, width: "100%" }}>
                        <Work sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                        <Select
                          variant="standard"
                          displayEmpty
                          fullWidth
                          value={experience}
                          onChange={(e) => {
                            const val = e.target.value;
                            setExperience(val);
                            handleFilterChange("experience", val === "" ? -1 : parseInt(val));
                          }}
                          disableUnderline
                          MenuProps={{ disablePortal: true }}
                          renderValue={(selected) => {
                            if (selected === "" || selected === -1) return <Typography color="text.secondary" variant="body2">Experience</Typography>;
                            if (selected === "0" || selected === 0) return <Typography variant="body2">Fresher</Typography>;
                            return <Typography variant="body2">{`${selected} Year${selected > 1 ? "s" : ""}`}</Typography>;
                          }}
                        >
                          <MenuItem value={-1}><em>Any Experience</em></MenuItem>
                          <MenuItem value={0}>Fresher (0-1 Year)</MenuItem>
                          {[1, 2, 3, 4, 5, 7, 10].map((val) => (
                            <MenuItem key={val} value={val}>{val}+ Years</MenuItem>
                          ))}
                        </Select>
                      </Box>

                      {!isMobile && <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 30, alignSelf: "center" }} />}

                      {/* Location Input */}
                      <Box sx={{ flex: 1.2, display: "flex", alignItems: "center", px: 2, width: "100%" }}>
                        <LocationOn sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                        <TextField
                          variant="standard"
                          fullWidth
                          placeholder="Location..."
                          value={location}
                          onChange={(e) => {
                            setLocation(e.target.value);
                            setCurrentPage(1);
                          }}
                          InputProps={{ disableUnderline: true }}
                        />
                      </Box>

                      {/* Search Button */}
                      <Button
                        variant="contained"
                        onClick={() => {
                          setCurrentPage(1);
                          setIsSearchExpanded(false);
                        }}
                        sx={{
                          borderRadius: "100px",
                          px: 4,
                          py: 1.5,
                          fontSize: '0.95rem',
                          boxShadow: 'none',
                          width: isMobile ? "100%" : "auto"
                        }}
                      >
                        Search
                      </Button>
                    </>
                  )}
                </Paper>
              </ClickAwayListener>
              {isMobile && !isSearchExpanded && (
                <IconButton 
                  onClick={() => setMobileFilterOpen(true)}
                  sx={{ bgcolor: "primary.main", color: "white", "&:hover": { bgcolor: "primary.dark" } }}
                >
                  <FilterList />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Job cards grid */}
          <Grid container spacing={3}>
            {paginatedJobs.map((job) => (
              <Grid item xs={12} sm={6} lg={4} key={job.id}>
                <Card
                  sx={{
                    borderRadius: "16px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: "1px solid #eef2f6",
                    position: 'relative',
                    overflow: 'hidden',
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 30px rgba(0, 139, 139, 0.12)",
                      borderColor: 'primary.light'
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                      <IconButton size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                        <Work sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "800",
                        color: "text.primary",
                        lineHeight: 1.2,
                        mb: 0.5,
                        fontSize: '1.1rem'
                      }}
                    >
                      {job.title}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.main", fontWeight: 700, letterSpacing: '0.2px' }}>
                      {job.company_name}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <LocationOn sx={{ fontSize: 18, color: "primary.light" }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {job.location || "Remote"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Business sx={{ fontSize: 18, color: "primary.light" }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {(() => {
                            const mode = job.work_mode === 'office' ? 'In Office' : (job.work_mode || "Full Time");
                            return mode.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
                          })()} • {job.experience_range || "0-1"} Yrs
                        </Typography>
                      </Box>

                      {job.salary && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CurrencyRupee sx={{ fontSize: 18, color: "secondary.main" }} />
                          <Typography variant="body2" color="text.primary" fontWeight="700">
                            {job.salary}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 'auto' }}>
                       {job.work_mode && (
                         <Chip 
                           label={job.work_mode === 'office' ? 'Office' : job.work_mode} 
                           size="small" 
                           sx={{ 
                             fontSize: '11px', 
                             height: '24px', 
                             bgcolor: job.work_mode === 'remote' ? '#e8f5e9' : (job.work_mode === 'hybrid' ? '#fff3e0' : '#e3f2fd'),
                             color: job.work_mode === 'remote' ? '#2e7d32' : (job.work_mode === 'hybrid' ? '#e65100' : '#1565c0'),
                             fontWeight: 700,
                             textTransform: 'uppercase',
                             borderRadius: '6px'
                           }} 
                         />
                       )}
                       {job.experience_range && (
                         <Chip 
                           label={`${job.experience_range} YRS`} 
                           size="small" 
                           sx={{ 
                             fontSize: '11px', 
                             height: '24px', 
                             bgcolor: '#f0f4f8',
                             color: '#475467',
                             fontWeight: 700,
                             borderRadius: '6px'
                           }} 
                         />
                       )}
                    </Box>
                  </CardContent>

                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      disableElevation
                      endIcon={<span>→</span>}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: "700",
                        py: 1.2,
                        fontSize: '0.9rem',
                        bgcolor: 'primary.main',
                        transition: 'all 0.2s',
                        "&:hover": {
                          bgcolor: "primary.dark",
                          gap: 1
                        },
                      }}
                      onClick={() => {
                        if (isAuthenticated) {
                          navigate(`/jobs/${job.id}`);
                        } else {
                          setModalOpen(true);
                        }
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No jobs message */}
          {filteredJobs.length === 0 && (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Typography variant="h6" color="text.secondary">
                No jobs found matching your criteria
              </Typography>
              <Button 
                sx={{ mt: 2, textTransform: "none" }}
                onClick={handleResetFilters}
              >
                Clear all filters
              </Button>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, value) => {
                  setCurrentPage(value);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontWeight: '700',
                    borderRadius: '8px',
                    '&.Mui-selected': {
                      boxShadow: '0 4px 10px rgba(0, 139, 139, 0.3)',
                    }
                  }
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Mobile Drawer */}
      <Drawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: "20px", borderTopRightRadius: "20px", maxHeight: "80vh", p: 3, overflowY: "auto" }
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1, pt: 1 }}>
          <Typography variant="h6" fontWeight="bold">All Filters</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button 
              variant="text" 
              size="small" 
              onClick={handleResetFilters}
              sx={{ textTransform: "none", fontWeight: "bold" }}
            >
              Clear Filters
            </Button>
            <Button onClick={() => setMobileFilterOpen(false)}>Done</Button>
          </Box>
        </Box>
        <FilterSection />
      </Drawer>

      {/* Login Required Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Login Required</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Please login first to view job details and apply for this position.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setModalOpen(false)} color="inherit" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={() => navigate("/login")}
            variant="contained"
            sx={{ borderRadius: "8px", textTransform: "none", px: 4 }}
          >
            Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Modal */}
      <Dialog
        open={Boolean(modalFilterType)}
        onClose={() => setModalFilterType(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", minHeight: "60vh" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ textTransform: "capitalize" }}>
            {modalFilterType === "department" ? "Department" : "Location"}
          </Typography>
          <IconButton onClick={() => setModalFilterType(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, maxHeight: "60vh", overflowY: "auto" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${modalFilterType}...`}
            value={modalSearchQuery}
            onChange={(e) => setModalSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size="small" />
                </InputAdornment>
              ),
              sx: { borderRadius: "100px", mb: 3, bgcolor: '#f5f5f5', px: 2, '& fieldset': { border: 'none' } }
            }}
          />
          <Grid container spacing={1}>
            {(modalFilterType === "department" ? allDepartments : allLocations)
              .filter(item => item.toLowerCase().includes(modalSearchQuery.toLowerCase()))
              .map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={tempModalFilters.includes(item)}
                        onChange={() => handleModalCheckboxChange(item)}
                        size="small"
                        sx={{ color: '#666', '&.Mui-checked': { color: 'primary.main' } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", fontSize: '0.875rem' }}>
                        {item}
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                          ({(modalFilterType === "department" ? deptCounts[item] : locCounts[item]) || 0})
                        </Typography>
                      </Typography>
                    }
                  />
                </Grid>
              ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #f0f0f0", justifyContent: 'space-between' }}>
          <Button 
            onClick={() => setTempModalFilters([])}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
          >
            Clear All
          </Button>
          <Box>
            <Button 
              onClick={() => setModalFilterType(null)} 
              color="inherit" 
              sx={{ textTransform: "none", mr: 1, fontWeight: 700 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleApplyModalFilters} 
              variant="contained" 
              disabled={!isApplyEnabled}
              sx={{ borderRadius: "100px", textTransform: "none", px: 4, fontWeight: 700 }}
            >
              Apply
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Jobs;
